import React from 'react';
import PropTypes from 'prop-types';
import Avatar from 'sharedComponents/Avatar';
import AssociateLogo from 'sharedComponents/AssociateLogo';
import { CheckIcon, BagIcon, KeyActionIcon, EmailIcon, ClientsCalendarIcon } from 'sharedComponents/Icons';
import { ListItemIcon, Typography } from '@material-ui/core';
import { getInitials, getPriceString, mapClientContacts, toTitleCase, formatDateWithDotMMDDYYYY } from 'utils';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import { ClientCentralPropTypes, GroupClientsFiltersPropTypes } from 'utils/PropTypesValues';
import { clientAssetsQueryParams } from 'sharedComponents/FilterChips/filterUtils';
import { STANLEY_ELIGIBLE_GROUP_ID, NMVIP_GROUP_TYPE, CLIENT_LIST_GROUP_TYPE } from 'constants/groups';
import { exclusiveInviteScript } from 'modules/clienteling/pages/NewMessage/utils';
import MobileAppIcon from '../Icons/MobileAppIcon';
import Pill from '../Pill';

const isSelected = (id, selectedClients = []) => selectedClients.find(({ profileId }) => profileId === id);

const ClientListItemGeneral = ({
  classes,
  client,
  selectionEnabled,
  withAlphabet,
  customClientClickAction,
  allowClickOnIcon,
  selectedClients,
  selectClient,
  beforeGoToProfilePage,
  updateRecentClients,
  history,
  match,
  filters,
  isUserDCA,
  isUserDigitalStylist,
  userBrand,
  groupType,
  isStanleyEligibleGroupEnabled,
  isDclDateMatchEnabled,
  isDigitalClientLeadEnabled,
  isMatchedClientsGroup,
  isNMVipGroupEnable,
  isMyClientsFirstTripGroup,
  isMyClientsTwoTripsGroup,
  isMyClientsRelationshipGroup,
  isMyChatClientsGroup,
  isMarketingPillsCcAndGlobalSearchEnabled,
}) => {
  const isAssociate = !!client?.isAssociate;

  const onTextClick = (client) => {
    if (customClientClickAction) {
      return customClientClickAction(client);
    }

    beforeGoToProfilePage();
    updateRecentClients(client);
    history.push(`/clients/universal-profile/${client.profileId}`);
  };

  const onAvatarClick = (client) => {
    if (selectionEnabled) return selectClient(client);

    if (allowClickOnIcon) {
      return onTextClick(client);
    }
  };

  const navigateToEmailQuickSend = (event, client) => {
    event.stopPropagation();
    const { emails, primaryEmail, profileId, firstName, lastName } = client;

    if (emails.length === 1 || primaryEmail) {
      history.push('/clienteling/new-email', {
        ...history.location.state,
        closeAndReturnToInbox: true,
        recipient: {
          clientData: client,
          profileId,
          fullName: `${firstName} ${lastName}`,
          contact: emails[0] || primaryEmail,
          exclusiveInviteScript,
        },
      });
      return;
    }

    if (emails?.length > 1 && !primaryEmail) {
      history.push('/clienteling/new-email', {
        ...history.location.state,
        closeAndReturnToInbox: true,
        recipient: {
          clientData: client,
          profileId,
          fullName: `${firstName} ${lastName}`,
          emails,
          exclusiveInviteScript,
        },
      });
    }
  };

  const clientAnnualSpendString = getPriceString(client?.annualSpend, true);
  const selectedAssets = filters?.withAssets?.split(',') || [];
  const showPointsAssetsBalance = selectedAssets.includes(clientAssetsQueryParams.point) && !!client.pointCardsBalance;
  const showPromoAssetsBalance = selectedAssets.includes(clientAssetsQueryParams.promo) && !!client.promoCardsBalance;
  const clientCity = client?.address?.city;
  const clientState = client?.address?.stateProvince;
  const preferredName = client?.preferredName;
  const isExistingStanleyProfile = !!client?.ucaStanleyProfiles?.length;

  const pillFilters = {
    NMVIP: isNMVipGroupEnable,
    'Matched Client': isDigitalClientLeadEnabled && userBrand === 'NM',
    'Re-matched': isMatchedClientsGroup && userBrand === 'NM',
    'Chat Client': isMyChatClientsGroup && isUserDigitalStylist,
    'Single Trip': isMyClientsFirstTripGroup,
    '2 Trips': isMyClientsTwoTripsGroup,
    '3+ Trips': isMyClientsRelationshipGroup,
  };

  const pills = [];

  if (client?.pills) {
    const enabledPills = client.pills.filter((pill) => pillFilters[pill] === undefined || pillFilters[pill]);
    pills.push(...enabledPills);
  }

  const isMatchedClient = !!client?.SAMatchDate;
  const isNMVip = !!client?.NmVipMatchDate;

  const showDclMatchDate = isDclDateMatchEnabled && isMatchedClient;
  const isStanleyEligibleClientsGroup =
    isStanleyEligibleGroupEnabled &&
    match.params.id === STANLEY_ELIGIBLE_GROUP_ID &&
    (isUserDCA || isUserDigitalStylist);

  const showUserAddress = clientCity || clientState;

  const showNewAvatar = groupType && (client?.isPrePopulated || isMatchedClient || isNMVip);
  const isNMVipClientWithDate =
    (groupType === NMVIP_GROUP_TYPE || groupType === CLIENT_LIST_GROUP_TYPE) && !!client?.NmVipMatchDate;

  const showNMVipMatchDate = isNMVipGroupEnable && isNMVipClientWithDate;

  if (isMarketingPillsCcAndGlobalSearchEnabled && client?.marketingEvents) {
    pills.push(...client?.marketingEvents);
  }

  const renderCityState = () => (
    <Typography
      display="inline"
      data-testid="city-state-info"
      className={classes.cityFontSize}
      component="span"
      color="textSecondary"
      noWrap
    >
      {clientCity}
      {clientCity && clientState && ', '}
      {clientState}
    </Typography>
  );

  const renderMatchDateField = (matchDate) => (
    <Typography className={classes.text} variant="body2" color="textSecondary">
      <ClientsCalendarIcon className={classes.calendarIcon} /> {formatDateWithDotMMDDYYYY(matchDate)}
    </Typography>
  );

  return (
    <ListItem
      disableGutters
      key={client.profileId}
      className={classes.link}
      data-testid="group-clients-list-item-general"
    >
      <ListItemAvatar onClick={() => onAvatarClick(client)} data-testid="group-clients-select">
        {selectionEnabled && isSelected(client.profileId, selectedClients) ? (
          <Avatar data-testid="avatar" className={classes.activeAvatar}>
            <CheckIcon className={classes.icon} />
          </Avatar>
        ) : (
          <Avatar data-testid="avatar" className={showNewAvatar ? classes.newAvatar : classes.avatar}>
            {getInitials(`${client.firstName} ${client.lastName}`)}
          </Avatar>
        )}
      </ListItemAvatar>
      <ListItemText onClick={() => onTextClick(client)} data-testid="group-clients-details">
        <div className={classes.header}>
          <Typography className={classes.nameWrapper} color="textPrimary">
            {toTitleCase(`${client.firstName} ${client.lastName}`)}
            {preferredName && (
              <span className={classes.preferredName} style={{ textOverflow: 'ellipsis' }}>{`(${preferredName})`}</span>
            )}
          </Typography>

          <span className={classes.stanleyIconsWrapper}>
            {userBrand === 'NM' && isExistingStanleyProfile && <MobileAppIcon />}
            {isAssociate && <AssociateLogo />}
            {isStanleyEligibleClientsGroup && (
              <EmailIcon
                onClick={(event) => navigateToEmailQuickSend(event, client)}
                color="inherit"
                customName="isStanleyEligibleGroupIcon"
                data-testid="isStanleyEligibleGroupIcon"
              />
            )}
          </span>
        </div>

        <Typography className={classes.text} variant="body2" color="textSecondary">
          {mapClientContacts(client.phoneNumbers || client.phoneNumber, client.emails || client.email, '•')}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {clientAnnualSpendString && (
            <span className={classes.annualSpend}>Annual spend: {clientAnnualSpendString}</span>
          )}
          {!!pills.length && (
            <span className={classes.pills}>
              {pills.map((label) => (
                <Pill label={label} key={label} />
              ))}
            </span>
          )}
          {showUserAddress && renderCityState()}
        </Typography>
        {showPointsAssetsBalance && (
          <Typography variant="body2" color="textSecondary" noWrap className={classes.assetsBalance}>
            Point card balance: {getPriceString(client.pointCardsBalance, true)}
          </Typography>
        )}
        {showPromoAssetsBalance && (
          <Typography variant="body2" color="textSecondary" noWrap className={classes.assetsBalance}>
            Promo card balance: {getPriceString(client.promoCardsBalance, true)}
          </Typography>
        )}
        {showDclMatchDate && renderMatchDateField(client.SAMatchDate)}
        {showNMVipMatchDate && renderMatchDateField(client.NmVipMatchDate)}
      </ListItemText>
      {client.connectedWithMe && (
        <ListItemIcon
          classes={{ root: withAlphabet ? classes.menuItemIconContactWithMe : classes.menuItemIconContactWithMeSmall }}
          data-testid="connect-icon"
        >
          <KeyActionIcon className={classes.icon} />
        </ListItemIcon>
      )}
      {client.conciergeItems && (
        <ListItemIcon
          classes={{ root: withAlphabet ? classes.menuItemIcon : classes.menuItemIconSmall }}
          data-testid="concierge-cart-icon"
        >
          <BagIcon className={classes.icon} />
        </ListItemIcon>
      )}
    </ListItem>
  );
};

ClientListItemGeneral.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  client: ClientCentralPropTypes,
  selectClient: PropTypes.func,
  selectedClients: PropTypes.arrayOf(PropTypes.object),
  selectionEnabled: PropTypes.bool,
  allowClickOnIcon: PropTypes.bool,
  beforeGoToProfilePage: PropTypes.func,
  updateRecentClients: PropTypes.func,
  filters: GroupClientsFiltersPropTypes,
  isUserDCA: PropTypes.bool,
  isUserDigitalStylist: PropTypes.bool,
  userBrand: PropTypes.string,
  isStanleyEligibleGroupEnabled: PropTypes.bool,
  isDclDateMatchEnabled: PropTypes.bool,
  isDigitalClientLeadEnabled: PropTypes.bool,
  isMatchedClientsGroup: PropTypes.bool,
  isNMVipGroupEnable: PropTypes.bool,
  isMyClientsFirstTripGroup: PropTypes.bool,
  isMyClientsTwoTripsGroup: PropTypes.bool,
  isMyClientsRelationshipGroup: PropTypes.bool,
  isMyChatClientsGroup: PropTypes.bool,
  isMarketingPillsCcAndGlobalSearchEnabled: PropTypes.bool,
};

ClientListItemGeneral.defaultProps = {
  selectClient: () => {},
  selectionEnabled: false,
  allowClickOnIcon: false,
  isStanleyEligibleGroupEnabled: false,
  isDclDateMatchEnabled: false,
  selectedClients: [],
  updateRecentClients: () => {},
  beforeGoToProfilePage: () => null,
  classes: {},
};

export default React.memo(ClientListItemGeneral);
